import { z } from 'zod';
import type { Look } from './look.js';

/**
 * Bot Kaki: the friendly NPC who stands in each public room, welcomes
 * visitors and explains what the room is for.
 *
 * A guide is not a player. It has no account, cannot be traded with or
 * respected, and never moves. Its scripts live here so the server (which
 * speaks them) and the client (which lists the topics on its card) agree.
 * Rooms are matched by their seed key, so a room with no script has no guide.
 */

export const GUIDE_NAME = 'Bot Kaki';
/** Shown on the guide's card so nobody mistakes it for a person. */
export const GUIDE_TAG = 'NPC';
/** Chat command that talks to the guide: `:bot`, `:bot 2`, `:bot drinks`. */
export const GUIDE_COMMAND = ':bot';
/** Avatar ids of guides start with this; real user ids never do. */
export const GUIDE_ID_PREFIX = 'npc:';

export interface GuideTopic {
  title: string;
  /** What Bot Kaki says. Keep it under MAX_CHAT_LENGTH. */
  body: string;
}

export interface GuideScript {
  /** Said to each newcomer; `{name}` becomes their display name. */
  greeting: string;
  topics: readonly GuideTopic[];
  /** Said now and then to the room while someone is in it. */
  tips: readonly string[];
  /** Outfit changes (indexes into the wardrobe) so each room's guide looks its own. */
  look: Partial<Look>;
}

const BASICS: GuideTopic = {
  title: 'How do I get around?',
  body: 'Click a tile to walk there. Click a person to see their card, click furniture to use it. Type in the chat bar to talk, and use the wave and dance buttons to say hi without words.',
};

/** Topics every guide can answer, after the room-specific ones. */
export const COMMON_TOPICS: readonly GuideTopic[] = [
  BASICS,
  {
    title: 'Who are you?',
    body: "I'm Bot Kaki, a robot NPC, not a real player! I stay in my spot and help newcomers. Click me, or type :bot in chat, to ask about this room.",
  },
];

const script = (
  greeting: string,
  topics: readonly GuideTopic[],
  tips: readonly string[],
  look: Partial<Look> = {},
): GuideScript => ({ greeting, topics, tips, look });

/** Keyed by the room's seed key (see the server's db/seeds). */
export const GUIDE_SCRIPTS: Readonly<Record<string, GuideScript>> = Object.freeze({
  cafe: script(
    'Hello {name}, welcome to the Kopitiam! I\'m Bot Kaki, an NPC. Grab a seat and order a kopi, or click me to learn what you can do here.',
    [
      {
        title: 'Ordering drinks',
        body: 'Walk next to a counter, stand still, and pick a drink: Kopi, Kopi O, Kopi C, Teh Tarik, Milo or Bandung. It stays in your hand for about two minutes.',
      },
      {
        title: 'Working as a barista',
        body: 'Click a barista station to clock in as a Kopi Barista. Answer the orders correctly to earn Kaki Dollars, and level up for better pay.',
      },
      {
        title: 'Music and the stage',
        body: 'Click the jukebox to queue a song for the whole room. The raised area at the back is the open-mic stage, so walk up and say something!',
      },
    ],
    [
      'Tip: the orange couches are the best spot to chat with new kakis.',
      'Tip: click the jukebox to queue a song. Everyone takes turns, so it stays fair.',
      'Tip: a barista shift here earns Kaki Dollars for your wardrobe and pets.',
    ],
    { hairColor: 3, topColor: 2 },
  ),
  swimming_pool: script(
    'Hi {name}! Welcome to the Swimming Pool. I\'m Bot Kaki, an NPC. Walk right into the water for a swim!',
    [
      {
        title: 'Going for a swim',
        body: 'Walk onto the blue water tiles and your avatar will start swimming. Stand still in the water and you\'ll tread water instead.',
      },
      {
        title: 'Building your own pool',
        body: 'Pool water, coping and deck tiles are all in the shop under Pool. Lay a few water tiles together to build a pool of any shape in your own room.',
      },
    ],
    ['Tip: grab a drink from the poolside counter before you dry off.', 'Tip: the umbrella tables are a good spot to sit out the sun.'],
    { hairColor: 7, topColor: 4 },
  ),
  arcade: script(
    'Welcome to the Arcade Corner, {name}! I\'m Bot Kaki, an NPC. Click a cabinet to play and chase the high scores.',
    [
      {
        title: 'Playing the machines',
        body: 'Click a cabinet to start a run. Your score is checked by the server, so only honest runs make the leaderboard.',
      },
      {
        title: 'More games',
        body: 'The Pasar Malam Funfair has even more machines, plus carousel horses and popcorn. Open the room list to visit.',
      },
    ],
    ['Tip: bean bags by the wall are good for watching friends play.'],
    { hairColor: 1, topColor: 12 },
  ),
  funfair: script(
    'Step right up, {name}! Welcome to the Pasar Malam Funfair. I\'m Bot Kaki, an NPC, and I know every stall.',
    [
      {
        title: 'The arcade machines',
        body: 'There are four to try: Kopi Catch, MRT Dash, Kueh Stack and Chope. Click a machine to start a run and beat the high scores.',
      },
      {
        title: 'Rides and snacks',
        body: 'Hop on a carousel horse, grab popcorn or visit the balloon stand. They are here for fun and photos.',
      },
    ],
    ['Tip: each machine has its own high score table.', 'Tip: East Coast Park has a Dragon Boat Race for a team of kakis.'],
    { hairColor: 6, topColor: 16 },
  ),
  east_coast: script(
    'Hello {name}, welcome to East Coast Park! I\'m Bot Kaki, an NPC. Feel the sea breeze and try the Dragon Boat Race.',
    [
      {
        title: 'Dragon Boat Race',
        body: 'Click the dragon boat to open a lobby. Race with 1 to 3 kakis: everyone readies up, then paddle together as fast as you can.',
      },
      {
        title: 'Relaxing',
        body: 'Sit on a bean bag, grab popcorn and chat. It is a nice place to meet people between shifts.',
      },
    ],
    ['Tip: the race is more fun with friends. Ask around in chat!'],
    { hairColor: 2, topColor: 5 },
  ),
  mall: script(
    'Welcome to Orchard Kaki Mall, {name}! I\'m Bot Kaki, an NPC. Shopping, sales and air-con, all in one place.',
    [
      {
        title: 'Working as a cashier',
        body: 'Click a cash register to clock in as a Mall Cashier. A shift is 8 tasks, and correct answers earn Kaki Dollars.',
      },
      {
        title: 'Player shops',
        body: "Click the mall directory to browse shops other players have opened. You can buy items from them with Kaki Dollars.",
      },
    ],
    ['Tip: check the sale signs and clothes racks for a bit of mall atmosphere.', 'Tip: the directory lists every player shop.'],
    { hairColor: 4, topColor: 10 },
  ),
  office_tower: script(
    'Good day, {name}, and welcome to Raffles Place Office Tower. I\'m Bot Kaki, an NPC, here to help you find your desk.',
    [
      {
        title: 'Clocking in',
        body: 'Sit at an office desk to clock in as an Office Clerk. A shift has 8 tasks, and each correct answer pays Kaki Dollars.',
      },
      {
        title: 'Levelling up',
        body: 'Every correct task earns job XP. Higher levels pay more, and a streak of right answers adds a bonus.',
      },
    ],
    ['Tip: answer carefully. Very fast answers are treated as automated and marked wrong.', 'Tip: the water cooler is a good place to chat.'],
    { hairColor: 0, topColor: 1 },
  ),
  hawker_centre: script(
    'Welcome to the Hawker Centre, {name}! I\'m Bot Kaki, an NPC. Hungry? Chicken rice, laksa, kopi and teh are all around.',
    [
      {
        title: 'Cooking at a stall',
        body: 'Click a hawker stall to clock in as a Hawker Chef. It pays a little better than most jobs at the start.',
      },
      {
        title: 'Drinks',
        body: 'Stand next to a counter or drinks station to order a kopi or teh. You can also work the drinks station as a Kopi Barista.',
      },
    ],
    ['Tip: grab a stool at a round table and invite a kaki to join you.'],
    { hairColor: 7, topColor: 14 },
  ),
  game_lounge: script(
    'Hi {name}, welcome to the Game Lounge! I\'m Bot Kaki, an NPC. Pick a table and challenge a kaki.',
    [
      {
        title: 'Which games are here?',
        body: 'Tic-tac-toe, Connect Four, Checkers, Chinese Chess and Snakes & Ladders (up to 8 players). Click a table to sit and start or join a game.',
      },
      {
        title: 'Finding an opponent',
        body: 'Sit at a table and wait, or ask in chat. Once another player sits at the same table, you can begin.',
      },
    ],
    ['Tip: Snakes & Ladders is great for a big group.', 'Tip: you can also just watch a game if you want to learn.'],
    { hairColor: 9, topColor: 3 },
  ),
  court_hall: script(
    'Welcome to the Court Hall, {name}. I\'m Bot Kaki, an NPC. Here we play Shadow Court, the game of hidden roles.',
    [
      {
        title: 'What is Shadow Court?',
        body: 'A game of hidden roles, secret votes and missions. Some players have secrets, and everyone must work out who to trust.',
      },
      {
        title: 'Joining a table',
        body: 'Six round tables seat five to ten players each. Sit at any table to join, or click it to just watch.',
      },
    ],
    ['Tip: it is a social game, so use chat and read the room.'],
    { hairColor: 10, topColor: 18 },
  ),
  bazaar_salon: script(
    'Hello {name}, welcome to the Bazaar Salon! I\'m Bot Kaki, an NPC. Come trade spices and jewels.',
    [
      {
        title: 'What is Spice Bazaar?',
        body: 'A gem-trading game of jewels and nobles. Collect gems, buy cards and impress nobles. The first to 15 prestige points wins.',
      },
      {
        title: 'Joining a table',
        body: 'Six tables seat two to four players each. Sit at any table to play, or click it to watch.',
      },
    ],
    ['Tip: watching a game or two is a good way to learn.'],
    { hairColor: 11, topColor: 22 },
  ),
  jukebox_lounge: script(
    'Hey {name}, welcome to the Jukebox Lounge! I\'m Bot Kaki, an NPC. Pick a song and everyone listens together.',
    [
      {
        title: 'Requesting songs',
        body: 'Click a jukebox and choose a track. Everyone shares one queue, and songs are taken from players in turn, so nobody hogs the music.',
      },
      {
        title: 'Queue limits',
        body: 'You can have up to 5 songs waiting at once, and the whole queue holds 30. There are genres like Mandopop, Rock, Jazz and Lo-fi.',
      },
    ],
    ['Tip: the couches are a great spot to sit and listen.'],
    { hairColor: 12, topColor: 6 },
  ),
  news_studio: script(
    'Welcome to the Kopi News Studio, {name}! I\'m Bot Kaki, an NPC. Click a TV to catch up on the news.',
    [
      {
        title: 'The three TVs',
        body: 'Game Updates shows what is new in Kopi Kaki. Live Stats shows the world by the numbers. Game Events shows what is happening right now.',
      },
    ],
    ['Tip: check Game Updates after each visit to see what changed.'],
    { hairColor: 13, topColor: 4 },
  ),
  pet_shop: script(
    'Hello {name}, welcome to the Pet Shop! I\'m Bot Kaki, an NPC. Looking for a new friend?',
    [
      {
        title: 'Adopting a pet',
        body: 'Click the pet shop counter to see the species: cat, dog, parrot, rabbit, hamster, tortoise and otter. Prices are in Kaki Dollars.',
      },
      {
        title: 'Looking after pets',
        body: 'Pets follow you around and need food, play, baths and sleep. A neglected pet gets sad, but never dies or runs away.',
      },
    ],
    ['Tip: earn Kaki Dollars from a job first, then come back to adopt.'],
    { hairColor: 14, topColor: 9 },
  ),
  dog_run: script(
    'Hi {name}, welcome to Bishan Dog Run! I\'m Bot Kaki, an NPC. Bring your pet and let them run free.',
    [
      {
        title: 'What can pets do here?',
        body: 'Play fetch and run on the open grass, and dig for treasure at the sand pits.',
      },
      {
        title: "I don't have a pet",
        body: 'Visit the Pet Shop @ Tiong Bahru to adopt one, then bring them back here.',
      },
    ],
    ['Tip: playing with your pet keeps them happy.'],
    { hairColor: 15, topColor: 11 },
  ),
  void_deck: script(
    'Hello {name}, welcome to the Void Deck! I\'m Bot Kaki, an NPC. Just like the one under the HDB block downstairs.',
    [
      {
        title: 'What is here?',
        body: 'Stone benches and tables, a mama shop, a durian stall, a satay grill, a bicycle, and the famous dragon playground.',
      },
      {
        title: 'What do I do?',
        body: 'Sit on a bench and chat with the neighbours. It is a relaxed place to meet people.',
      },
    ],
    ['Tip: the void deck is the traditional place for a good lepak.'],
    { hairColor: 1, topColor: 7 },
  ),
  merlion_park: script(
    'Welcome to Merlion Park, {name}! I\'m Bot Kaki, an NPC. Say cheese by the Merlion!',
    [
      {
        title: 'What is here?',
        body: 'The Merlion by the bay, rain trees, lanterns and a kopi kiosk. It is a scenic spot for meeting people.',
      },
    ],
    ['Tip: a great place for a group photo with your kakis.'],
    { hairColor: 3, topColor: 15 },
  ),
});

export function guideScriptFor(seedKey: string | null | undefined): GuideScript | undefined {
  return seedKey ? GUIDE_SCRIPTS[seedKey] : undefined;
}

export function isGuideId(id: string): boolean {
  return id.startsWith(GUIDE_ID_PREFIX);
}

/** All the topics a guide offers in a room: its own, then the shared ones. */
export function guideTopics(script: GuideScript): readonly GuideTopic[] {
  return [...script.topics, ...COMMON_TOPICS];
}

export const guideClientMessageSchema = z.discriminatedUnion('t', [
  /**
   * Talk to the guide in your room: by topic (index into `guideTopics`), by a
   * few words typed after `:bot`, or with neither to hear the topic list.
   */
  z.object({
    t: z.literal('guide:ask'),
    topic: z.number().int().min(0).max(31).optional(),
    query: z.string().trim().max(60).optional(),
  }),
]);

/** Picks a topic from what a player typed: a list number (1-based), or the best word match. */
export function findGuideTopic(script: GuideScript, query: string): number | null {
  const topics = guideTopics(script);
  const text = query.trim().toLowerCase();
  if (!text) return null;
  if (/^\d+$/.test(text)) {
    const index = Number(text) - 1;
    return index >= 0 && index < topics.length ? index : null;
  }
  const words = text.split(/[^a-z0-9']+/).filter((w) => w.length >= 3);
  let best: { index: number; score: number } | null = null;
  topics.forEach((topic, index) => {
    const haystack = `${topic.title} ${topic.body}`.toLowerCase();
    const title = topic.title.toLowerCase();
    let score = 0;
    for (const word of words) {
      if (title.includes(word)) score += 3;
      else if (haystack.includes(word)) score += 1;
    }
    if (score > (best?.score ?? 0)) best = { index, score };
  });
  return (best as { index: number } | null)?.index ?? null;
}
